/**
 * Run async tasks with a maximum concurrency to avoid exhausting browser
 * connection limits (e.g. Chrome's net::ERR_INSUFFICIENT_RESOURCES).
 * @param {Array<T>} items - Items to process
 * @param {number} concurrency - Max concurrent executions (e.g. 6 for Chrome per-origin limit)
 * @param {function(T): Promise} fn - Async function to run for each item
 * @returns {Promise<Array<{status: 'fulfilled', value: *}|{status: 'rejected', reason: *}>>} Same shape as Promise.allSettled
 */
export async function runWithConcurrency(items, concurrency, fn) {
  if (items.length === 0) return [];
  const results = new Array(items.length);
  let index = 0;
  async function worker() {
    while (index < items.length) {
      const i = index++;
      const item = items[i];
      try {
        const value = await fn(item);
        results[i] = { status: "fulfilled", value };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  }
  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

/**
 * Creates a limiter that allows at most maxConcurrency async operations at a time.
 * Use for any caller (e.g. fetchTagData) so all requests share one cap app-wide.
 * @param {number} maxConcurrency - Max concurrent operations (e.g. 4 for Chrome per-origin headroom)
 * @returns {{ run: (asyncFn: () => Promise<T>) => Promise<T> }}
 */
export function createConcurrencyLimiter(maxConcurrency) {
  let inFlight = 0;
  const queue = [];

  function runNext() {
    if (queue.length === 0 || inFlight >= maxConcurrency) return;
    inFlight++;
    const { asyncFn, resolve, reject } = queue.shift();
    Promise.resolve()
      .then(asyncFn)
      .then(
        (value) => {
          resolve(value);
          inFlight--;
          runNext();
        },
        (err) => {
          reject(err);
          inFlight--;
          runNext();
        }
      );
  }

  return {
    run(asyncFn) {
      return new Promise((resolve, reject) => {
        queue.push({ asyncFn, resolve, reject });
        runNext();
      });
    },
  };
}
